import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { Header } from "@/components/layout/Header";
import { BlogBreadcrumb } from "@/components/seo/BlogBreadcrumb";
import { Footer } from "@/components/layout/Footer";
import { SEOContentBlock } from "@/components/seo/SEOContentBlock";
import { RelatedReads } from "@/components/seo/RelatedReads";
import { getRelatedPosts } from "@/content/blogPosts";
import { Button } from "@/components/ui/button";
import { ArrowLeft, CalendarDays } from "lucide-react";
import heroImage from "@/assets/gallery/tubing-canyon-view.jpg";
import springImg from "@/assets/blog/spring-break-river-ranch.jpg";
import summerImg from "@/assets/tubing/group-tubing-fun.webp";
import fallImg from "@/assets/gallery/river-view.jpg";

const PUBLISH_DATE = "May 9, 2026";
const CANONICAL = "/blog/best-time-to-tube-guadalupe-river";
const TITLE = "Best Time to Tube the Guadalupe River — Month-by-Month Guide | Son's";
const DESCRIPTION =
  "When is the best time to tube the Guadalupe River? A month-by-month guide to river flow, weather, crowds, and the best tubing season near New Braunfels.";

const FAQS = [
  {
    q: "When is the best time to tube the Guadalupe River?",
    a: "Late May through early September is peak tubing season on the Guadalupe River near New Braunfels. June and July offer the best mix of warm weather and reliable river flow. Spring break and shoulder seasons are great for smaller crowds.",
  },
  {
    q: "Is the Guadalupe River open year-round?",
    a: "Son's Guadalupe properties are open year-round for cabana rentals, glamping, and tent camping. Tubing depends on river flow and weather — we run tubing daily through the warm months and case-by-case in spring and fall.",
  },
  {
    q: "How do I check Guadalupe River flow before my trip?",
    a: "Our team monitors river conditions daily. Call or check our website for current updates. Generally, flow is strongest after rain and lowest in late summer dry spells — but we float a calm, family-friendly stretch that's tube-friendly across most flow ranges.",
  },
  {
    q: "What month has the best weather for tubing?",
    a: "June and early July typically offer the warmest, sunniest days with the most reliable river flow. August can be hot but still excellent on the water, and late May offers fewer crowds with great weather.",
  },
];

const schemaJson = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://sonsguadalupe.com/" },
        { "@type": "ListItem", position: 2, name: "Blog", item: "https://sonsguadalupe.com/blog" },
        { "@type": "ListItem", position: 3, name: "Best Time to Tube the Guadalupe River — A Month-by-Month Guide", item: `https://sonsguadalupe.com/blog/best-time-to-tube-guadalupe-river` },
      ],
    },
    {
      "@type": "BlogPosting",
      headline: "Best Time to Tube the Guadalupe River — Month-by-Month Guide",
      description: DESCRIPTION,
      image: "https://sonsguadalupe.com/og-image.png",
      author: { "@type": "Organization", name: "Son's Guadalupe" },
      datePublished: "2026-05-09",
      mainEntityOfPage: { "@type": "WebPage", "@id": `https://sonsguadalupe.com${CANONICAL}` },
      keywords:
        "best time to tube guadalupe river, guadalupe river flow, when to tube new braunfels, guadalupe river season, tubing season texas",
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQS.map(({ q, a }) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    },
  ],
};

const BlogBestTimeToTube = () => {
  const related = getRelatedPosts("best-time-to-tube-guadalupe-river", 3);
  return (
    <div className="min-h-screen">
      <SEOHead title={TITLE} description={DESCRIPTION} canonical={CANONICAL} ogImage="/og/blog-best-time.jpg" schemaJson={schemaJson} />
      <Header />
      <BlogBreadcrumb />
      <main className="pt-24 pb-16">
        <div className="relative h-[55vh] min-h-[400px]">
          <img fetchPriority="high" loading="eager" decoding="async" src={heroImage} alt="Tubing the Guadalupe River through the canyon near New Braunfels" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12">
            <div className="container-narrow mx-auto">
              <div className="flex items-center gap-2 text-white/80 text-sm mb-3">
                <CalendarDays className="w-4 h-4" /> {PUBLISH_DATE}
              </div>
              <h1 className="font-heading text-3xl md:text-5xl font-bold text-white max-w-4xl leading-tight">
                Best Time to Tube the Guadalupe River — A Month-by-Month Guide
              </h1>
            </div>
          </div>
        </div>

        <article className="section-padding bg-background">
          <div className="container-narrow mx-auto max-w-3xl px-4">
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-primary hover:underline mb-8">
              <ArrowLeft className="w-4 h-4" /> Back to Blog
            </Link>

            <div className="prose prose-lg max-w-none text-foreground space-y-6">
              <p className="text-xl leading-relaxed">
                Wondering about <strong>the best time to tube the Guadalupe River</strong>? The short answer: late May through early September. The longer answer depends on what kind of trip you want — peak summer energy, fewer crowds, or a quiet shoulder-season float. Here's how each month actually feels on the river near New Braunfels.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">March — Spring Break</h2>
              <p>
                Spring break on the Guadalupe is unpredictable but magical when it hits. Water can be cool, weather warm. Best for warm-weather years and brave floaters. Cabana rentals are easier to book than in summer.
              </p>

              <img src={springImg} alt="Spring on the Guadalupe River at Son's River Ranch" loading="lazy" className="w-full rounded-2xl shadow-elevated my-8" />

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">April — May: Shoulder Season</h2>
              <p>
                One of our favorite times. Wildflowers, mild temps, fewer crowds, and the river is typically running well. Late May is when tubing season really wakes up.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">June — Peak Summer Begins</h2>
              <p>
                The single best month for most families. Long sunny days, warm water, reliable river flow, and the full Son's experience open — tube rentals, cabanas, glamping, and tent camping on River Road all running at full speed.
              </p>

              <img src={summerImg} alt="Group tubing fun on the Guadalupe River in summer" loading="lazy" className="w-full rounded-2xl shadow-elevated my-8" />

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">July — Texas Summer in Full Swing</h2>
              <p>
                Hot, busy, and amazing. Book early — especially weekends and around July 4. A shaded cabana rental on the Guadalupe River is the move when temps climb past 95°F.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">August — Late Summer</h2>
              <p>
                Still excellent for tubing, with slightly lower flows in dry years. Crowds thin a bit after the first week as Texas schools head back. A great month for last-minute family getaways near New Braunfels.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">September — Labor Day & After</h2>
              <p>
                Labor Day weekend is the unofficial closing party of tubing season. After that, it shifts to weekends-only depending on weather. Beautiful light, warm afternoons, no crowds.
              </p>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">October — February: Off-Season Magic</h2>
              <p>
                Tubing slows down, but cabanas, glamping near New Braunfels, and tent camping on River Road keep going. Cooler weather is perfect for hiking, fishing, fire pits, and quiet riverside weekends.
              </p>

              <img src={fallImg} alt="Quiet Guadalupe River view in fall near River Road" loading="lazy" className="w-full rounded-2xl shadow-elevated my-8" />

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">Quick Picks</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Best overall:</strong> June</li>
                <li><strong>Smallest crowds + great weather:</strong> Late May, late August</li>
                <li><strong>Family vibe:</strong> Mid-June through mid-July</li>
                <li><strong>Big group / party:</strong> July weekends</li>
                <li><strong>Quiet weekend:</strong> September</li>
              </ul>

              <h2 className="font-heading text-2xl font-bold text-foreground mt-10">Tubing the Guadalupe River FAQs</h2>
              <div className="space-y-6">
                {FAQS.map((f) => (
                  <div key={f.q}>
                    <h3 className="font-heading text-lg font-semibold text-foreground">{f.q}</h3>
                    <p className="mt-2">{f.a}</p>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
                <Link to="/book-now"><Button variant="default" size="lg" className="text-lg px-10 py-6">Book Your Float</Button></Link>
                <Link to="/tubing"><Button variant="outline" size="lg" className="text-lg px-10 py-6">Tubing Info</Button></Link>
              </div>
            </div>
          </div>
        </article>
        <RelatedReads posts={related} />

      </main>
      <SEOContentBlock
        eyebrow="Plan Your Visit"
        heading="The Easiest Way to Tube the Guadalupe River"
        paragraphs={[
          "Pick your month, book a cabana on the Guadalupe River, and let Son's handle the rest. Tubing near New Braunfels works best when you have a private basecamp — and that's exactly what we built.",
        ]}
        links={[
          { label: "Cabanas", to: "/cabanas" },
          { label: "Tubing", to: "/tubing" },
          { label: "Glamping", to: "/glamping" },
          { label: "FAQs", to: "/faqs" },
        ]}
      />
      <Footer />
    </div>
  );
};

export default BlogBestTimeToTube;
